import React, { useState } from "react";
import { Clear, KeyboardArrowLeft, Visibility } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import * as z from "zod";
import { ObjectField, TextInput } from "~/components/Form";
import { optionalObject } from "~/domain/common";
import type { LogTag } from "~/domain/logs";
import {
  checkIsLogRangeTag,
  checkIsTimestampedLogTag,
  LogTagChip,
  pairTagWithType,
} from "~/domain/logs";
import { useForm } from "~/lib/form";
import type { Log, TagUpdateRequest } from "~/lqs";
import { useDeleteTag, useUpdateTag } from "~/lqs";
import {
  useFormatPlaybackTimestamp,
  useLoadedPlaybackSource,
  usePlaybackSettings,
  usePlayerActions,
} from "~/player";
import { getEventHandlerProps } from "~/utils/get-event-handler-props";
import {
  durationTimestampSchema,
  rawTimestampSchema,
  TimestampEndAdornment,
} from "../../components/timestamp-selection";

export function ActiveTagSection({
  log,
  tag,
  readonly,
}: {
  log: Log;
  tag: LogTag;
  readonly: boolean;
}) {
  const formatPlaybackTimestamp = useFormatPlaybackTimestamp();

  const playerActions = usePlayerActions();

  const updateTag = useUpdateTag(log.id, tag.id);
  const deleteTag = useDeleteTag(log.id, tag.id);

  const disabled = updateTag.isLoading || deleteTag.isLoading;

  const { enqueueSnackbar } = useSnackbar();

  const tagForm = useTagForm(tag, disabled, (values) => {
    updateTag.mutate(values, {
      onSuccess() {
        enqueueSnackbar("Tag updated", { variant: "success" });
      },
      onError() {
        enqueueSnackbar("Unable to update tag", { variant: "error" });
      },
    });
  });

  const deleteTagHandlerProps = getEventHandlerProps(
    "onClick",
    !disabled &&
      function handleDeleteTag(): void {
        deleteTag.mutate(undefined, {
          onError() {
            enqueueSnackbar("Unable to delete tag", { variant: "error" });
          },
        });
      },
  );

  function handleClearActiveTag(): void {
    playerActions.clearActiveTag();
  }

  function createSeekHandler(timestamp: bigint) {
    return function handleSeek(): void {
      playerActions.seek(timestamp);
    };
  }

  return (
    <Stack spacing={3}>
      <Button
        color="primary"
        variant="text"
        fullWidth
        startIcon={<KeyboardArrowLeft />}
        onClick={handleClearActiveTag}
        disabled={disabled}
        sx={{ justifyContent: "start" }}
      >
        Return to Tags
      </Button>
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <LogTagChip
          log={log}
          {...pairTagWithType(tag)}
          readonly
          formatTimestamp={formatPlaybackTimestamp}
          useHashColor
        />
        {!readonly && (
          <Box sx={{ position: "relative" }}>
            <Tooltip title="Delete tag">
              <span>
                <IconButton {...deleteTagHandlerProps} color="error">
                  <Clear />
                </IconButton>
              </span>
            </Tooltip>
            {deleteTag.isLoading && (
              <CircularProgress
                size="2rem"
                sx={{
                  position: "absolute",
                  inset: 0,
                  margin: "auto",
                }}
              />
            )}
          </Box>
        )}
      </Stack>
      {checkIsTimestampedLogTag(tag) && (
        <Stack direction="row" spacing={1}>
          <Button
            color="primary"
            variant="outlined"
            size="small"
            onClick={createSeekHandler(tag.startTime)}
          >
            Seek: {formatPlaybackTimestamp(tag.startTime)}
          </Button>
          {checkIsLogRangeTag(tag) && (
            <Button
              color="primary"
              variant="outlined"
              size="small"
              onClick={createSeekHandler(tag.endTime)}
            >
              Seek: {formatPlaybackTimestamp(tag.endTime)}
            </Button>
          )}
        </Stack>
      )}
      <Stack
        spacing={2}
        component="form"
        noValidate
        onSubmit={tagForm.handleSubmit}
      >
        <TextInput
          name="startTime"
          label={readonly ? "Start Time (read only)" : undefined}
          readonly={readonly}
          disabled={disabled}
          size="small"
          value={tagForm.startTimeField.value}
          onChange={tagForm.startTimeField.onChange}
          onKeyDown={tagForm.startTimeField.onKeyDown}
          errorMessage={tagForm.startTimeField.error}
          endAdornment={
            <TimestampEndAdornment
              value={tagForm.startTimeField.value}
              action={
                !readonly && (
                  <Tooltip title="Preview">
                    <span>
                      <IconButton
                        type="button"
                        edge="end"
                        size="small"
                        {...tagForm.startTimeField.previewHandlerProps}
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )
              }
            />
          }
        />
        <TextInput
          name="endTime"
          label={readonly ? "End Time (read only)" : undefined}
          readonly={readonly}
          disabled={disabled}
          size="small"
          value={tagForm.endTimeField.value}
          onChange={tagForm.endTimeField.onChange}
          onKeyDown={tagForm.endTimeField.onKeyDown}
          errorMessage={tagForm.endTimeField.error}
          endAdornment={
            <TimestampEndAdornment
              value={tagForm.endTimeField.value}
              action={
                !readonly && (
                  <Tooltip title="Preview">
                    <span>
                      <IconButton
                        type="button"
                        edge="end"
                        size="small"
                        {...tagForm.endTimeField.previewHandlerProps}
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )
              }
            />
          }
        />
        <ObjectField
          control={tagForm.contextField.control}
          name="context"
          label={readonly ? "Context (read only)" : undefined}
          readonly={readonly}
          disabled={disabled}
        />
        {!readonly && (
          <Stack>
            <LoadingButton
              type="submit"
              loading={updateTag.isLoading}
              disabled={disabled}
              color="primary"
              variant="outlined"
              size="small"
              sx={{ alignSelf: "baseline" }}
            >
              Update
            </LoadingButton>
            {!updateTag.isLoading && tagForm.dirty && (
              <FormHelperText>
                Press{" "}
                <Typography
                  variant="inherit"
                  component="span"
                  sx={{ fontWeight: "bold" }}
                >
                  Update
                </Typography>{" "}
                to save changes
              </FormHelperText>
            )}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

interface TimestampField {
  value: string | null;
  error: string | undefined;
  validate: boolean;
}

type TimestampParseResult =
  | [error: string, data: bigint | null | undefined, fieldValue: string | null]
  | [error: undefined, data: bigint | null, fieldValue: string | null];

function useTagForm(
  tag: LogTag,
  disabled: boolean,
  onSubmit: (
    values: Pick<TagUpdateRequest, "startTime" | "endTime" | "context">,
  ) => void,
) {
  const playerActions = usePlayerActions();

  const timestampManager = useTimestampManager(tag);

  const [startTimeField, setStartTimeField] = useState<TimestampField>({
    value: timestampManager.startTime.value,
    error: undefined,
    validate: false,
  });
  const [endTimeField, setEndTimeField] = useState<TimestampField>({
    value: timestampManager.endTime.value,
    error: undefined,
    validate: false,
  });

  function handleStartTimeChange(value: string | null): void {
    let error;
    if (startTimeField.validate) {
      [error] = timestampManager.parseStartTime(value, endTimeField.value);
    }

    setStartTimeField({
      ...startTimeField,
      value,
      error,
    });
  }

  const startTimeChanged =
    startTimeField.value !== timestampManager.startTime.value;

  const previewStartTimeHandlerProps = getEventHandlerProps(
    "onClick",
    !disabled &&
      startTimeChanged &&
      startTimeField.value != null &&
      function handlePreviewStartTime(): void {
        const [error, startTime, value] = timestampManager.parseStartTime(
          startTimeField.value,
          endTimeField.value,
        );

        if (error == null) {
          timestampManager.startTime.update(startTime);

          if (startTime != null) {
            playerActions.seek(startTime);
          }
        }

        setStartTimeField({
          ...startTimeField,
          value,
          validate: error != null,
          error,
        });
      },
  );

  function handleEndTimeChange(value: string | null): void {
    let error;
    if (endTimeField.validate) {
      [error] = timestampManager.parseEndTime(startTimeField.value, value);
    }

    setEndTimeField({
      ...endTimeField,
      value,
      error,
    });
  }

  const endTimeChanged = endTimeField.value !== timestampManager.endTime.value;

  const previewEndTimeHandlerProps = getEventHandlerProps(
    "onClick",
    !disabled &&
      endTimeChanged &&
      endTimeField.value != null &&
      function handlePreviewEndTime(): void {
        const [error, endTime, value] = timestampManager.parseEndTime(
          startTimeField.value,
          endTimeField.value,
        );

        if (error == null) {
          timestampManager.endTime.update(endTime);

          if (endTime != null) {
            playerActions.seek(endTime);
          }
        }

        setEndTimeField({
          ...endTimeField,
          value,
          validate: error != null,
          error,
        });
      },
  );

  const primaryForm = useForm({
    schema: z.object({
      context: optionalObject,
    }),
    values: {
      context: tag.context,
    },
    onSubmit(values) {
      const [startTimeError, startTime, startTimeValue] =
        timestampManager.parseStartTime(
          startTimeField.value,
          endTimeField.value,
          true,
        );
      const [endTimeError, endTime, endTimeValue] =
        timestampManager.parseEndTime(startTimeField.value, endTimeField.value);

      const isValid = startTimeError == null && endTimeError == null;

      setStartTimeField({
        ...startTimeField,
        value: startTimeValue,
        error: startTimeError,
        validate: !isValid,
      });
      setEndTimeField({
        ...endTimeField,
        value: endTimeValue,
        error: endTimeError,
        validate: !isValid,
      });

      if (startTimeError == null) {
        timestampManager.startTime.update(startTime);
      }

      if (endTimeError == null) {
        timestampManager.endTime.update(endTime);
      }

      if (!isValid) {
        return;
      }

      onSubmit({ startTime, endTime, ...values });
    },
    onError() {
      const [startTimeError, startTime, startTimeValue] =
        timestampManager.parseStartTime(
          startTimeField.value,
          endTimeField.value,
          true,
        );
      const [endTimeError, endTime, endTimeValue] =
        timestampManager.parseEndTime(startTimeField.value, endTimeField.value);

      setStartTimeField({
        ...startTimeField,
        value: startTimeValue,
        error: startTimeError,
        validate: true,
      });
      setEndTimeField({
        ...endTimeField,
        value: endTimeValue,
        error: endTimeError,
        validate: true,
      });

      if (startTimeError == null) {
        timestampManager.startTime.update(startTime);
      }

      if (endTimeError == null) {
        timestampManager.endTime.update(endTime);
      }
    },
  });

  function createTimestampFieldKeyPressHandler(
    previewHandlerProps:
      | typeof previewStartTimeHandlerProps
      | typeof previewEndTimeHandlerProps,
  ) {
    return function handleTimestampFieldKeyPress(e: React.KeyboardEvent): void {
      if (e.key !== "Enter") {
        return;
      }

      e.preventDefault();

      if (previewHandlerProps.disabled) {
        return;
      }

      previewHandlerProps.onClick();
    };
  }

  const {
    formState: {
      dirtyFields: { context: contextDirty = false },
    },
  } = primaryForm;

  const startTimeDirty = startTimeChanged || timestampManager.startTime.dirty;
  const endTimeDirty = endTimeChanged || timestampManager.endTime.dirty;

  return {
    handleSubmit: primaryForm.handleSubmit,
    dirty: startTimeDirty || endTimeDirty || contextDirty,
    startTimeField: {
      value: startTimeField.value,
      onChange: handleStartTimeChange,
      onKeyDown: createTimestampFieldKeyPressHandler(
        previewStartTimeHandlerProps,
      ),
      error: startTimeField.error,
      dirty: startTimeDirty,
      previewHandlerProps: previewStartTimeHandlerProps,
    },
    endTimeField: {
      value: endTimeField.value,
      onChange: handleEndTimeChange,
      onKeyDown: createTimestampFieldKeyPressHandler(
        previewEndTimeHandlerProps,
      ),
      error: endTimeField.error,
      dirty: endTimeDirty,
      previewHandlerProps: previewEndTimeHandlerProps,
    },
    contextField: {
      control: primaryForm.control,
    },
  };
}

function useTimestampManager(tag: LogTag) {
  const { bounds } = useLoadedPlaybackSource();

  const formatPlaybackTimestamp = useFormatPlaybackTimestamp();

  const { displayFormat } = usePlaybackSettings();

  let formatValue: (value: bigint) => string;
  let schemaFactory;
  if (displayFormat === "elapsed") {
    formatValue = (value) => formatPlaybackTimestamp(value, "elapsed");
    schemaFactory = durationTimestampSchema.bind(null, bounds.startTime);
  } else {
    formatValue = String;
    schemaFactory = rawTimestampSchema;
  }

  const [timestamps, setTimestamps] = useState({
    startTime: tag.startTime,
    endTime: tag.endTime,
  });

  const timestampSchema = schemaFactory(
    {
      value: bounds.startTime,
      formatted: formatValue(bounds.startTime),
      exclusive: false,
    },
    {
      value: bounds.endTime,
      formatted: formatValue(bounds.endTime),
      exclusive: false,
    },
  ).nullable();

  return {
    formatValue,
    timestampSchema,
    parseStartTime(
      startTimeValue: string | null,
      endTimeValue: string | null,
      ignoreEndTime = false,
    ): TimestampParseResult {
      const result = timestampSchema.safeParse(startTimeValue);

      if (!result.success) {
        return [
          result.error.issues[0].message ?? "",
          undefined,
          startTimeValue,
        ];
      } else {
        const { data: startTime } = result;

        let error;
        if (!ignoreEndTime && startTime != null) {
          const endTimeResult = timestampSchema.safeParse(endTimeValue);

          if (
            endTimeResult.success &&
            endTimeResult.data != null &&
            startTime >= endTimeResult.data
          ) {
            error = `Must be less than ${formatValue(endTimeResult.data)}`;
          }
        }

        return [
          error,
          startTime,
          startTime == null ? null : formatValue(startTime),
        ];
      }
    },
    parseEndTime(
      startTimeValue: string | null,
      endTimeValue: string | null,
    ): TimestampParseResult {
      const result = timestampSchema.safeParse(endTimeValue);

      if (!result.success) {
        return [result.error.issues[0].message ?? "", undefined, endTimeValue];
      } else {
        const { data: endTime } = result;

        let error;
        if (endTime != null) {
          const startTimeResult = timestampSchema.safeParse(startTimeValue);

          if (startTimeResult.success) {
            const { data: startTime } = startTimeResult;

            if (startTime == null) {
              error = "Cannot only provide end time";
            } else if (endTime <= startTime) {
              error = `Must be greater than ${formatValue(startTime)}`;
            }
          }
        }

        return [
          error,
          result.data,
          result.data == null ? null : formatValue(result.data),
        ];
      }
    },
    startTime: {
      value:
        timestamps.startTime == null ? null : formatValue(timestamps.startTime),
      dirty: timestamps.startTime !== tag.startTime,
      update(startTime: bigint | null): void {
        setTimestamps((prev) => ({ ...prev, startTime }));
      },
    },
    endTime: {
      value:
        timestamps.endTime == null ? null : formatValue(timestamps.endTime),
      dirty: timestamps.endTime !== tag.endTime,
      update(endTime: bigint | null): void {
        setTimestamps((prev) => ({ ...prev, endTime }));
      },
    },
  };
}
